import { useRecoilValue } from 'recoil';
import { vendorState } from 'state';

import { useJobTagsEnabled } from './use-job-tags-enabled';

/**
 * Whether the current user may put tags on a job belonging to `vendorId`.
 *
 * `useJobTagsEnabled` alone answers "does MY processor have job tags?", and
 * the pickers offer MY processor's tags. That is the wrong question on any
 * surface that can show another processor's job — an admin on a tenant's job
 * page, a processor booking at somebody else's plant — where it would offer
 * tags the server refuses to store on that job. So: the module is on, AND the
 * job is the active vendor's own.
 *
 * Presentation only, like `useJobTagsEnabled`. The server enforces the same
 * rule in `services/job-tags/job-tag-writes`.
 * @param vendorId the vendor that owns (or will own) the job.
 * @returns true when the tag pickers should be offered for that job.
 */
export function useCanTagJobsOf(vendorId: string | null | undefined): boolean {
  const jobTagsEnabled = useJobTagsEnabled();
  const activeVendor = useRecoilValue(vendorState);

  return jobTagsEnabled && !!vendorId && activeVendor?._id === vendorId;
}
